import QRCode from 'qrcode';
import { useEffect, useState } from 'react';
import { navigate } from '../lib/router';
import { baht, main } from '../lib/text';
import type { LineStatus } from '../lib/types';
import { Back } from '../ui/icons';
import type { GuestState } from './GuestApp';
import { Header } from './Header';

const LABEL: Record<LineStatus, string> = {
  queued: 'รอคิว · In line',
  cooking: 'กำลังทำ · Cooking',
  ready: 'เสร็จแล้ว · Ready',
  served: 'เสิร์ฟแล้ว · Served',
  cancelled: 'ยกเลิก · Cancelled',
};

/** The order quest: every round with each dish's live status, and the running total. */
export function Tracker({ s }: { s: GuestState }) {
  const lines = s.orders.flatMap((o) => o.lines);
  const live = lines.filter((l) => l.status !== 'cancelled');
  const done = live.filter((l) => l.status === 'served').length;
  const total = live.reduce((t, l) => t + l.unitSatang * l.qty, 0);
  const pct = live.length ? Math.round((done / live.length) * 100) : 0;

  return (
    <main className="screen">
      <Header s={s} sub="Your orders" />
      <div className="row" style={{ padding: '4px 20px 12px' }}>
        <button className="icon-btn" onClick={() => navigate('/t')} aria-label="Back to the menu"><Back /></button>
        <h1 className="display" style={{ margin: 0, fontSize: 24, flex: 1, paddingLeft: 12 }}>ออร์เดอร์ · Orders</h1>
      </div>

      <section className="quest" aria-live="polite">
        {s.pending && (
          <div className="warn">รอร้านยืนยันออร์เดอร์ · Waiting for the restaurant to confirm your takeout order {s.guest.tableName}</div>
        )}
        {s.guest.kind === 'takeout' && !s.pending && lines.length > 0 && lines.filter((l) => l.status !== 'cancelled').every((l) => l.status === 'ready' || l.status === 'served') && (
          <div className="ticket" style={{ background: '#BFEBD0' }}><span className="display" style={{ fontSize: 20 }}>พร้อมรับแล้ว! · Ready for pickup: {s.guest.tableName}</span></div>
        )}
        <div className="ticket">
          <div className="row"><span className="display">เสิร์ฟแล้ว {done}/{live.length} · served</span><span className="display">{baht(total)}</span></div>
          <div style={{ height: 14, background: '#E7E9F0', border: '2px solid var(--ink)', borderRadius: 8, overflow: 'hidden' }}>
            <div style={{ width: `${pct}%`, height: '100%', background: 'var(--green)', transition: 'width .4s' }} />
          </div>
        </div>

        {[...s.orders].reverse().map((o) => (
          <article key={o.id} className="ticket">
            <div className="row">
              <span className="display" style={{ fontSize: 18 }}>#{o.number}</span>
              <span className="muted" style={{ fontSize: 13 }}>{new Date(o.placedAt).toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' })}{o.placedBy === 'staff' ? ' · by staff' : ''}</span>
            </div>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {o.lines.map((l) => (
                <li key={l.id} className="row" style={{ alignItems: 'flex-start' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <span style={{ fontWeight: 600 }}>{l.qty}× {main(l.name)}</span>
                    {(l.choices.length > 0 || l.note) && (
                      <span className="muted" style={{ fontSize: 12 }}>{[...l.choices.map((c) => main(c.name)), l.note].filter(Boolean).join(' · ')}</span>
                    )}
                  </div>
                  <span className={`status ${l.status}`}>{LABEL[l.status]}</span>
                </li>
              ))}
            </ul>
          </article>
        ))}

        <button className="cta light" onClick={() => navigate('/t')}>สั่งเพิ่ม · Order more</button>
        {s.orders.length > 0 && (
          <button className="cta light" style={{ background: '#fff' }} onClick={() => navigate('/t/bill')}>เช็คบิล · Bill &amp; pay</button>
        )}
        {s.guest.inviteUrl && s.guest.kind !== 'takeout' && <Invite url={s.guest.inviteUrl} />}
      </section>
    </main>
  );
}

/** Friends who arrive later scan this from a phone already at the table (it works for this sitting only). */
function Invite({ url }: { url: string }) {
  const [open, setOpen] = useState(false);
  const [svg, setSvg] = useState('');
  useEffect(() => { if (open && !svg) void QRCode.toString(url, { type: 'svg', margin: 1 }).then(setSvg); }, [open, svg, url]);
  return (
    <div className="ticket" style={{ alignItems: 'center' }}>
      <button className="ghost" style={{ color: 'var(--ink)', borderColor: 'var(--ink)', width: '100%' }} aria-expanded={open} onClick={() => setOpen(!open)}>
        ชวนเพื่อนร่วมโต๊ะ · Invite friends to this table
      </button>
      {open && (svg
        ? <div className="qr" style={{ border: '3px solid var(--ink)' }} role="img" aria-label="Invite QR code" dangerouslySetInnerHTML={{ __html: svg }} />
        : <div className="spinner" />)}
      {open && <span className="muted" style={{ fontSize: 13, textAlign: 'center' }}>ให้เพื่อนสแกนเพื่อสั่งร่วมกัน · Friends scan this to order together</span>}
    </div>
  );
}
